import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { customHistory } from "../../framework/customHistory";
import {
    GridColDef,
    GridPaginationModel,
    GridRenderCellParams,
    GridSortModel,
} from "@mui/x-data-grid-premium";
import {
    TransportOrderNotificationListItemDto,
    TransportOrderNotificationState,
} from "../../models/transport/transportOrderNotification";
import { getDateTime } from "../transport/components/transportUtils";
import { MuiDataGrid } from "../framework/muiDataGrid";
import { transportOrderStateNames } from "../../models/transport/transportOrder";
import { CustomPagination } from "../framework/editableDataGrid/customPagination";
import { useAppSelector } from "../../framework/customStore";
import {
    confirmAllMobileToDesktopNotifications,
    confirmTransportOrderNotification,
} from "../../services/transportOrderService";
import { confirmNotification } from "../../services/notificationService";
import type { ApiResponse } from "../../services/apiClient";
import { PagedResult } from "../../models/pagedResult/pagedResult";
import { Base } from "../../framework/base";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import {
    NotificationListItemDto,
    NotificationState,
} from "../../models/notification/notification";
import { showApiError } from "../framework/formUtils";

interface NotificationsTableProps {
    notifications: TransportOrderNotificationListItemDto[];
    desktopNotifications: NotificationListItemDto[];
    currentPage: number;
    pageSize: number;
    totalCount: number;
    onPageChange: (page: number) => void;
    onPageSizeChange: (pageSize: number) => void;
    fetchNotifications: (
        sortField?: string,
        sortOrder?: string
    ) => Promise<PagedResult<TransportOrderNotificationListItemDto>>;
    onDesktopNotificationsChanged: () => void;
    hasTransportOrdersEnabled: boolean;
}

interface ContextData {
    vehicleRegisterNumber?: string;
    maintenanceTitle?: string;
    reminderDate?: string;
}

const parseContextJson = (contextJson: string): ContextData => {
    try {
        return JSON.parse(contextJson) as ContextData;
    } catch {
        return {};
    }
};

export const NotificationsTable = ({
    notifications,
    desktopNotifications,
    currentPage,
    pageSize,
    totalCount,
    onPageChange,
    onPageSizeChange,
    fetchNotifications,
    onDesktopNotificationsChanged,
    hasTransportOrdersEnabled,
}: NotificationsTableProps) => {
    const { t } = useTranslation();
    const location = useLocation();

    const confirmTransportOrderNotificationSafe: (
        id: string
    ) => Promise<ApiResponse<unknown>> = confirmTransportOrderNotification;

    const activeTab =
        new URLSearchParams(location.search).get("tab") === "transport" ? 1 : 0;
    const handleTabChange = useCallback(
        (_: React.SyntheticEvent, newValue: number) => {
            const tabValue = newValue === 1 ? "transport" : "general";
            customHistory.push(`${location.pathname}?tab=${tabValue}`);
        },
        [location.pathname]
    );
    const [confirmDisabled, setConfirmDisabled] = useState(false);
    const [acknowledgingIds, setAcknowledgingIds] = useState<Set<string>>(
        new Set()
    );
    const [desktopAcknowledgingIds, setDesktopAcknowledgingIds] = useState<
        Set<string>
    >(new Set());
    const employees = useAppSelector((state) => state.employees.employees);

    // Convert our pagination to MUI DataGrid format
    const paginationModel = useMemo(
        () => ({
            page: currentPage - 1, // MUI uses 0-based indexing
            pageSize: pageSize,
        }),
        [currentPage, pageSize]
    );

    const handlePaginationModelChange = useCallback(
        (model: GridPaginationModel) => {
            onPageChange(model.page + 1); // Convert back to 1-based indexing
            onPageSizeChange(model.pageSize);
        },
        [onPageChange, onPageSizeChange]
    );

    const handleConfirm = async() => {
        setConfirmDisabled(true);
        try {
            await confirmAllMobileToDesktopNotifications();
            fetchNotifications()
                .catch(() => {
                    showApiError(t("notifications.fetchFailed"));
                })
                .finally(() => {
                    setConfirmDisabled(false);
                });
        } catch (error) {
            setConfirmDisabled(false);
            console.error("Error confirming notifications!:", error);
        }
    };

    const handleAcknowledge = useCallback(
        async(notificationId: string) => {
            setAcknowledgingIds((prev) => {
                const next = new Set(prev);
                next.add(notificationId);
                return next;
            });

            try {
                await confirmTransportOrderNotificationSafe(notificationId);
                await fetchNotifications();
            } catch (error) {
                console.error("Error acknowledging notification:", error);
                showApiError(t("notifications.acknowledgeFailed"));
            } finally {
                setAcknowledgingIds((prev) => {
                    const next = new Set(prev);
                    next.delete(notificationId);
                    return next;
                });
            }
        },
        [fetchNotifications, t]
    );

    const handleDesktopAcknowledge = useCallback(
        async(notificationId: string, ownerId: string) => {
            setDesktopAcknowledgingIds((prev) => {
                const next = new Set(prev);
                next.add(notificationId);
                return next;
            });

            try {
                await confirmNotification(notificationId, {
                    targetObjectType: "Owner",
                    targetObjectId: ownerId,
                });
                onDesktopNotificationsChanged();
            } catch (error) {
                console.error("Error acknowledging notification:", error);
                showApiError(t("notifications.acknowledgeFailed"));
            } finally {
                setDesktopAcknowledgingIds((prev) => {
                    const next = new Set(prev);
                    next.delete(notificationId);
                    return next;
                });
            }
        },
        [onDesktopNotificationsChanged, t]
    );

    const handleSortModelChange = React.useCallback(
        (sortModel: GridSortModel) => {
            if (sortModel[0]) {
                fetchNotifications(
                    Base.capitalizeFirstChar(sortModel[0].field),
                    sortModel[0].sort
                ).catch(() => {
                    showApiError(t("notifications.fetchFailed"));
                });
            } else {
                fetchNotifications().catch(() => {
                    showApiError(t("notifications.fetchFailed"));
                });
            }
        },
        [fetchNotifications, t]
    );

    const getStateText = (state: number) => {
        if (state < TransportOrderNotificationState.Confirmed.valueOf()) {
            return t("notifications.unconfirmed");
        }
        if (state === TransportOrderNotificationState.Confirmed.valueOf()) {
            return t("notifications.confirmed");
        }
        return "-";
    };

    const getEmployeeName = (id: string) => {
        const employee = employees.find((i) => i.id === id);
        if (employee) {
            return employee.fullName;
        }

        return "-";
    };

    const desktopGridDef: GridColDef<NotificationListItemDto>[] = useMemo(
        () => [
            {
                headerName: t("common.created"),
                field: "createdAt",
                width: 150,
                renderCell: (
                    params: GridRenderCellParams<NotificationListItemDto>
                ) => {
                    return <div>{getDateTime(params.row?.createdAt)}</div>;
                },
            },
            {
                headerName: t("common.message"),
                field: "messageKey",
                sortable: false,
                type: "string",
                flex: 4,
                cellClassName: "wrapCell",
                renderCell: (
                    params: GridRenderCellParams<NotificationListItemDto>
                ) => {
                    const context = parseContextJson(params.row?.contextJson);

                    return (
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                width: "100%",
                            }}
                        >
                            <Typography variant="body2">
                                {t(params.row?.titleKey)}
                            </Typography>
                            {(context.vehicleRegisterNumber ||
                                context.maintenanceTitle) && (
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    {[
                                        context.vehicleRegisterNumber,
                                        context.maintenanceTitle,
                                        context.reminderDate,
                                    ]
                                        .filter(Boolean)
                                        .join(" — ")}
                                </Typography>
                            )}
                        </Box>
                    );
                },
            },
            {
                headerName: t("common.status"),
                field: "state",
                sortable: false,
                type: "string",
                flex: 1,
                renderCell: (
                    params: GridRenderCellParams<NotificationListItemDto>
                ) => {
                    const state = params.row?.state;
                    if (state === NotificationState.Confirmed) {
                        return <div>{t("notifications.confirmed")}</div>;
                    }
                    return <div>{t("notifications.unconfirmed")}</div>;
                },
            },
            {
                headerName: "",
                field: "acknowledge",
                sortable: false,
                filterable: false,
                width: 140,
                align: "right",
                headerAlign: "right",
                renderCell: (
                    params: GridRenderCellParams<NotificationListItemDto>
                ) => {
                    const id = params.row?.notificationId;
                    const isAcknowledging = id
                        ? desktopAcknowledgingIds.has(id)
                        : false;

                    return (
                        <Button
                            id={`acknowledge-desktop-notification-${id}`}
                            size="small"
                            variant="contained"
                            color="success"
                            disabled={!id || isAcknowledging}
                            onClick={() => {
                                if (id) {
                                    void handleDesktopAcknowledge(id, params.row?.ownerId);
                                }
                            }}
                        >
                            {t("notifications.acknowledge")}
                        </Button>
                    );
                },
            },
        ],
        [desktopAcknowledgingIds, handleDesktopAcknowledge, t]
    );

    const transportOrderGridDef: GridColDef<TransportOrderNotificationListItemDto>[] =
        useMemo(
            () => [
                {
                    headerName: t("common.created"),
                    field: "createdDateTime",
                    width: 150,
                    renderCell: (
                        params: GridRenderCellParams<TransportOrderNotificationListItemDto>
                    ) => {
                        return (
                            <div>
                                {getDateTime(params.row?.createdDateTime)}
                            </div>
                        );
                    },
                },
                {
                    headerName: t("transport.order.creator"),
                    field: "createdEmployeeId",
                    sortable: false,
                    type: "string",
                    flex: 1,
                    renderCell: (
                        params: GridRenderCellParams<TransportOrderNotificationListItemDto>
                    ) => {
                        return (
                            <div>
                                {getEmployeeName(params.row?.createdEmployeeId)}
                            </div>
                        );
                    },
                },
                {
                    headerName: t("common.message"),
                    field: "message",
                    sortable: false,
                    type: "string",
                    flex: 4,
                    cellClassName: "wrapCell",
                    renderCell: (
                        params: GridRenderCellParams<TransportOrderNotificationListItemDto>
                    ) => {
                        const driverComment =
                            typeof params.row?.driverComment === "string"
                                ? String(params.row.driverComment)
                                : "";

                        const orderStateKey =
                            params.row?.orderState !== undefined &&
                            params.row?.orderState !== null
                                ? (transportOrderStateNames[
                                    params.row.orderState
                                ] as unknown as string | undefined)
                                : undefined;

                        return (
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    width: "100%",
                                }}
                            >
                                <Typography variant="body2">
                                    {t(params.row?.message, {
                                        orderNro:
                                            params.row?.transportOrderNumber,
                                        vehicleName:
                                            params.row?.vehicleRegisterNumber,
                                        state: orderStateKey
                                            ? t(orderStateKey)
                                            : "-",
                                        employeeName: getEmployeeName(
                                            params.row?.createdEmployeeId
                                        ),
                                    })}
                                </Typography>

                                {driverComment.trim().length > 0 && (
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "flex-start",
                                            gap: 0.75,
                                            mt: 0.5,
                                        }}
                                    >
                                        <ChatBubbleOutlineIcon
                                            sx={{
                                                fontSize: 16,
                                                mt: "2px",
                                                color: "text.secondary",
                                            }}
                                        />
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ whiteSpace: "pre-wrap" }}
                                        >
                                            {driverComment}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        );
                    },
                },
                {
                    headerName: t("common.status"),
                    field: "state",
                    sortable: false,
                    type: "string",
                    flex: 1,
                    renderCell: (
                        params: GridRenderCellParams<TransportOrderNotificationListItemDto>
                    ) => {
                        return <div>{getStateText(params.row?.state)}</div>;
                    },
                },
                {
                    headerName: "",
                    field: "acknowledge",
                    sortable: false,
                    filterable: false,
                    width: 140,
                    align: "right",
                    headerAlign: "right",
                    renderCell: (
                        params: GridRenderCellParams<TransportOrderNotificationListItemDto>
                    ) => {
                        const id = params.row?.id;
                        const isAcknowledging = id
                            ? acknowledgingIds.has(id)
                            : false;

                        return (
                            <Button
                                id={`acknowledge-transport-notification-${id}`}
                                size="small"
                                variant="contained"
                                color="success"
                                disabled={!id || isAcknowledging}
                                onClick={() => {
                                    if (id) {
                                        void handleAcknowledge(id);
                                    }
                                }}
                            >
                                {t("notifications.acknowledge")}
                            </Button>
                        );
                    },
                },
            ],
            [acknowledgingIds, employees, handleAcknowledge, t]
        );

    const desktopNotificationsGrid = (
        <MuiDataGrid
            rows={desktopNotifications}
            columns={desktopGridDef}
            density="compact"
            rowSelection={false}
            disableColumnFilter
            getRowId={(row: NotificationListItemDto) =>
                row.notificationId
            }
            getRowHeight={() => "auto"}
            getEstimatedRowHeight={() => 56}
            sx={{
                "& .MuiDataGrid-cell": {
                    alignItems: "flex-start",
                    py: 1,
                },
                "& .MuiDataGrid-cell.wrapCell": {
                    whiteSpace: "normal",
                    lineHeight: 1.35,
                    overflowWrap: "anywhere",
                    wordBreak: "break-word",
                },
            }}
            toolbarProps={{
                title: t(
                    "notifications.desktopNotifications.tableHeader"
                ),
                enableColumnSelector: false,
                disableQuickFilter: true,
                disableToolbarColumnsButton: true,
            }}
        />
    );

    const transportOrderNotificationsGrid = (
        <MuiDataGrid
            rows={notifications}
            columns={transportOrderGridDef}
            density="compact"
            rowSelection={false}
            disableColumnFilter
            getRowHeight={() => "auto"}
            getEstimatedRowHeight={() => 56}
            pagination={true}
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={handlePaginationModelChange}
            rowCount={totalCount}
            pageSizeOptions={[25, 50, 100]}
            sortingMode="server"
            onSortModelChange={handleSortModelChange}
            customPagination={
                notifications?.length > 0 ? CustomPagination : null
            }
            sx={{
                "& .MuiDataGrid-cell": {
                    alignItems: "flex-start",
                    py: 1,
                },
                "& .MuiDataGrid-cell.wrapCell": {
                    whiteSpace: "normal",
                    lineHeight: 1.35,
                    overflowWrap: "anywhere",
                    wordBreak: "break-word",
                },
            }}
            toolbarProps={{
                title: t("notifications.masterList.tableHeader"),
                enableColumnSelector: false,
                disableQuickFilter: true,
                disableToolbarColumnsButton: true,
                customToolbarButtons:
                    notifications?.length > 0
                        ? [
                            {
                                key: "confirm-all",
                                color: "success",
                                variant: "contained",
                                onClick: handleConfirm,
                                children: t(
                                    "notifications.confirmAll"
                                ),
                                disabled: confirmDisabled,
                                sx: { marginRight: 1 },
                            },
                        ]
                        : null,
            }}
        />
    );

    if (!hasTransportOrdersEnabled) {
        return desktopNotificationsGrid;
    }

    return (
        <>
            <Tabs
                id="notifications-tabs"
                value={activeTab}
                onChange={handleTabChange}
                sx={{ mb: 1 }}
            >
                <Tab
                    id="notifications-tab-general"
                    label={t("notifications.desktopNotifications.tableHeader")}
                />
                <Tab
                    id="notifications-tab-transport"
                    label={t("notifications.masterList.tableHeader")}
                />
            </Tabs>
            {activeTab === 0 && desktopNotificationsGrid}
            {activeTab === 1 && transportOrderNotificationsGrid}
        </>
    );
};
